<script setup lang="ts">
import { computed } from 'vue'
import { T } from '@tolgee/vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import router from '@/router'
import { RouteName } from '@/routes'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useLiveOpsOverlay } from '@/composables/game/live-ops-overlay'

const props = defineProps<{
  show: boolean
  name?: string
  image?: string
  title?: string
}>()

const emit = defineEmits<{
  (e: 'click'): void
}>()

const { topStyle } = useFullScreenDimensions()
const { addHapticFeedback } = useHapticFeedback()
const {
  showLiveOpsOverlay,
  closeLiveOpsOverlay,
} = useLiveOpsOverlay()

function handleClose() {
  addHapticFeedback('light')
  emit('click')
}

function navigateToProfile() {
  if (showLiveOpsOverlay.value) {
    closeLiveOpsOverlay()
  }
  router.push(RouteName.PROFILE)
}

useWatchOverlays(computed(() => props.show))

</script>

<template>
  <Teleport to="#app">
    <Transition
      name="fade"
      mode="out-in"
    >
      <div
        v-if="show"
        class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 flex flex-col items-center justify-center bg-dark-most-bottom-layer/[.65] backdrop-blur-[4px] z-[800]"
        @click="handleClose"
      >
        <CrossCircleIcon
          class="w-6 h-6 min-w-[24px] absolute top-3 right-3 z-[6] text-white"
          :style="topStyle"
        />
        <div
          class="w-[90%] h-0 pb-[90%] absolute rounded-full bg-teal-primary blur-[150px] z-[2]"
        />
        <div
          class="flex flex-col items-center text-center z-[3] max-w-[350px]"
        >
          <h1
            class="text-light-white text-4xl leading-[42px] font-bold mb-3"
          >
            <template v-if="title">
              {{ title }}
            </template>
            <T
              v-else
              key-name="card_challenge.overlay.title"
              default-value="Badge unlocked!"
            />
          </h1>
          <p
            class="text-light-white font-semibold text-base general-sans-text"
          >
            {{ name }}
          </p>

          <p
            class="text-light-white text-base font-medium -tracking-[0.32px] mb-6 z-[3]"
          >
            <T
              key-name="card_challenge.overlay.description1"
              default-value="Head to your "
            />
            <span
              class="underline cursor-pointer"
              @click="navigateToProfile"
            >
              <T
                key-name="card_challenge.overlay.description_profile"
                default-value="profile"
              />
            </span>
            <T
              key-name="card_challenge.overlay.description2"
              default-value=" to see all your badges."
            />
          </p>
          <div
            class="size-[212px] rounded-full flex items-center justify-center overflow-hidden"
          >
            <div class="relative w-full h-full z-[3]">
              <!--            <img-->
              <!--              :src="BadgeDiskImage"-->
              <!--              class="z-[3]"-->
              <!--            >-->
              <img
                :src="image"
                class="absolute inset-0"
              >
            </div>
            <img
              class="absolute w-full h-auto"
              src="@/assets/images/goldwheel-overlay-bg.webp"
            >
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.bg-outer {
  background: url('@/assets/images/code.png') lightgray 50% / cover no-repeat;
}

.shadow-image {
  box-shadow: 0px 4px 4px 0px rgba(255, 255, 255, 0.40) inset;
}
</style>
